import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const PERMISSIONS_UPDATED_EVENT = 'permissions:updated';

// Shorthand builders for the new { view, create, edit, delete, approve } format
const NONE = { view: false, create: false, edit: false, delete: false, approve: false };
const VIEW = { view: true,  create: false, edit: false, delete: false, approve: false };
const FULL = { view: true,  create: true,  edit: true,  delete: true,  approve: true  };

const usePermissions = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPermissions();

    const handlePermissionsUpdate = () => {
      loadPermissions();
    };

    window.addEventListener(PERMISSIONS_UPDATED_EVENT, handlePermissionsUpdate);
    return () => {
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, handlePermissionsUpdate);
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const headers = { 'Content-Type': 'application/json' };
      if (window.__sessionId) headers['X-Session-ID'] = window.__sessionId;
      const res = await fetch(`${apiUrl}/permissions`, {
        headers,
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          setPermissions(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fallback defaults — mirrors PermissionsManagement.getPageAccess exactly so
  // the admin UI and the app always agree when no custom DB row exists.
  const getDefaultPermissions = (pageId, role) => {
    switch (pageId) {
      case 'dashboard':
      case 'login':
        return VIEW;

      case 'user-management':
      case 'permissions-management':
        return role === 'super_admin' ? FULL : NONE;

      case 'material-management':
        if (role === 'super_admin') return FULL;
        if (role === 'admin')       return { view: true,  create: true,  edit: true,  delete: true,  approve: false };
        if (role === 'manager')     return { ...NONE, view: true, edit: true };
        if (role === 'staff')       return VIEW;
        return NONE;

      case 'category-management':
      case 'vendor-management':
        if (['super_admin', 'admin'].includes(role)) return { view: true, create: true, edit: true, delete: true, approve: false };
        if (role === 'manager') return { view: true, create: true, edit: true, delete: false, approve: false };
        return NONE;

      case 'add-inventory':
      case 'edit-inventory':
        if (['super_admin', 'admin'].includes(role)) return FULL;
        if (role === 'manager') return { view: true, create: true, edit: true, delete: false, approve: false };
        return NONE;

      case 'view-inventory':
      case 'available-stock':
      case 'vendor-stock':
        if (['super_admin', 'admin', 'manager'].includes(role)) return { view: true, create: true, edit: true, delete: false, approve: false };
        if (role === 'staff') return VIEW;
        return NONE;

      case 'indian-billing':
      case 'us-billing':
        if (['super_admin', 'admin'].includes(role)) return FULL;
        if (role === 'manager') return { view: true, create: true, edit: false, delete: false, approve: false };
        return NONE;

      case 'view-estimates':
        if (['super_admin', 'admin', 'manager', 'staff'].includes(role)) return VIEW;
        return NONE;

      case 'inr-estimate':
      case 'usd-estimate':
      case 'estimate-cart':
        if (['super_admin', 'admin', 'manager'].includes(role)) return { view: true, create: true, edit: true, delete: false, approve: false };
        return NONE;

      case 'manual-rate-entry':
        if (['super_admin', 'admin'].includes(role)) return { view: true, create: true, edit: true, delete: false, approve: false };
        if (role === 'manager') return VIEW;
        return NONE;

      case 'barcode-generator':
        if (['super_admin', 'admin', 'manager', 'staff'].includes(role)) return VIEW;
        return NONE;

      default:
        if (role === 'super_admin') return FULL;
        if (role === 'admin')       return { ...NONE, view: true, edit: true };
        if (role === 'manager')     return VIEW;
        return NONE;
    }
  };

  // Check if the logged-in user has a specific permission for a page.
  // action: 'view' | 'create' | 'edit' | 'delete' | 'approve'
  const hasPermission = (pageId, action = 'view') => {
    if (!user) return false;

    const userRole = user.role;
    if (userRole === 'super_admin') return true;

    const pagePerms = permissions[pageId]?.[userRole] ?? getDefaultPermissions(pageId, userRole);
    return pagePerms[action] === true;
  };

  // Whether the user can navigate to / load a page at all
  const canAccessPage = (pageId) => hasPermission(pageId, 'view');

  // Legacy helper — prefer hasPermission directly
  const getPermissionLevel = (pageId) => {
    if (!user) return 'none';
    if (user.role === 'super_admin') return 'full';
    const p = permissions[pageId]?.[user.role] ?? getDefaultPermissions(pageId, user.role);
    if (p.delete || p.approve) return 'full';
    if (p.edit || p.create)    return 'edit';
    if (p.view)                return 'view';
    return 'none';
  };

  return {
    permissions,
    loading,
    hasPermission,
    canAccessPage,
    getPermissionLevel,
    loadPermissions
  };
};

export const notifyPermissionsUpdated = () => {
  window.dispatchEvent(new CustomEvent(PERMISSIONS_UPDATED_EVENT));
};

export default usePermissions;
